/**
 * Client-side continuation policy (spec 2026-09-21 §3.1/§3.4).
 *
 * The SERVER is the authority on every rule here — it re-validates the type,
 * the conversation, the depth and the MCP_AUTO_CONTINUE lever, and answers 409
 * when any of them fail. This module exists only so the client does not fire a
 * request that is certain to be refused. It never decides that something is
 * SAFE; it decides that something is worth asking for.
 */
export const CONTINUATION_TYPES = Object.freeze(['mcp_tool_call', 'mcp_server_register']);

/** Mirrors MCP_CONTINUATION_MAX_DEPTH in backend/src/services/mcp/mcpClientManager.js. */
export const MCP_CONTINUATION_MAX_DEPTH = 5;

function parsePayload(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) || {}; } catch { return {}; }
  }
  return {};
}

/**
 * @param {object} approved the row returned by POST /api/actions/:id/approve
 * @returns {{conversationId: string, continuationActionId: string, depth: number}|null}
 */
export function continuationRequest(approved) {
  if (!approved || !CONTINUATION_TYPES.includes(approved.action_type)) return null;
  if (typeof approved.result_text !== 'string' || approved.result_text.trim().length === 0) return null;
  const payload = parsePayload(approved.action_payload);
  if (!payload.conversationId) return null;
  const prior = Number(payload.continuationDepth);
  const depth = (Number.isFinite(prior) && prior > 0 ? Math.floor(prior) : 0) + 1;
  if (depth > MCP_CONTINUATION_MAX_DEPTH) return null;
  return { conversationId: String(payload.conversationId), continuationActionId: approved.id, depth };
}

// ── MCP_AUTO_CONTINUE, as the client last saw it ────────────────────────────
// GET /api/capabilities/status is fetched in exactly one place
// (hooks/useCapabilityStatus.js, mounted once in App.jsx); it publishes the
// lever here rather than growing a second fetch inside SolomonContext. Same
// module-store shape as setLastDiagramError in SolomonContext.jsx.
let _lever = 'unknown';

/** @param {string|null|undefined} status the `continuation.status` field */
export function setContinuationLever(status) {
  _lever = (status === 'ok' || status === 'disabled') ? status : 'unknown';
}

export function continuationLever() {
  return _lever;
}

/**
 * Which pending cards belong under the open conversation (spec §5). A card
 * with no conversationId in its payload — every legacy row and every non-chat
 * action type — keeps today's behaviour and is always shown.
 */
export function visiblePendingActions(actions, conversationId) {
  const list = Array.isArray(actions) ? actions : [];
  if (!conversationId) return list;
  return list.filter((a) => {
    const owner = parsePayload(a?.action_payload).conversationId;
    return !owner || String(owner) === String(conversationId);
  });
}

/**
 * The fire conditions the approved row itself cannot answer (spec §3.1/§3.6).
 * `unknown` does NOT block: MCP_AUTO_CONTINUE is opt-out, so a status the
 * client never managed to read must not silently kill the feature — the server
 * re-checks and answers 409 `auto_continue_disabled` if it really is off.
 */
export function shouldFireContinuation({
  request = null,
  currentConversationId = null,
  streamInFlight = false,
  leverStatus = null,
} = {}) {
  if (!request) return false;
  if (leverStatus === 'disabled') return false;
  // Two streams would share one "last message" and interleave into it.
  if (streamInFlight) return false;
  // A card approved from a workspace while another chat is open must not
  // stream its answer into the conversation on screen.
  if (!currentConversationId) return false;
  return String(request.conversationId) === String(currentConversationId);
}
